import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SeoHreflang — emite <link rel="canonical" /> y <link rel="alternate" hreflang />
 * para indicarle a Google que la página existe en es/en/pt vía ?lang=.
 *
 * Generado:
 *   <link rel="canonical"  href=".../PATH[?lang=current]" />
 *   <link rel="alternate" hreflang="es"        href=".../PATH" />
 *   <link rel="alternate" hreflang="en"        href=".../PATH?lang=en" />
 *   <link rel="alternate" hreflang="pt"        href=".../PATH?lang=pt" />
 *   <link rel="alternate" hreflang="x-default" href=".../PATH" />
 */

const SUPPORTED = ['es', 'en', 'pt'];

// Base canónica: siempre el dominio de prod. Los crawlers no indexan QA por separado.
const CANONICAL_BASE = 'https://www.proveminer.com';

const SeoHreflang = () => {
  const location = useLocation();
  // Detectar idioma actual: del pathname (window.location, real, antes de
  // que BrowserRouter strip-ee el basename), con fallback a ?lang= legacy.
  const realPath = typeof window !== 'undefined' ? window.location.pathname : location.pathname;
  const pathMatch = realPath.match(/^\/(en|pt)(\/|$)/);
  const params = new URLSearchParams(location.search);
  const rawLang = pathMatch ? pathMatch[1] : (params.get('lang') || '').toLowerCase();
  const currentLang = SUPPORTED.includes(rawLang) ? rawLang : 'es';

  // Path "limpio" sin prefijo de idioma (lo que se usa en todas las versiones).
  const cleanPath = realPath.replace(/^\/(en|pt)(?=\/|$)/, '') || '/';
  // Quitamos ?lang= legacy del query — la versión path-based es la canónica.
  const queryParams = new URLSearchParams(location.search);
  queryParams.delete('lang');
  const qs = queryParams.toString();
  const querySuffix = qs ? `?${qs}` : '';

  const buildUrl = (lang) => {
    const prefix = lang === 'es' ? '' : `/${lang}`;
    return `${CANONICAL_BASE}${prefix}${cleanPath}${querySuffix}`;
  };

  const canonicalUrl = buildUrl(currentLang);

  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
      {SUPPORTED.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={buildUrl(lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildUrl('es')} />
    </Helmet>
  );
};

export default SeoHreflang;
